import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { type DashboardRange, useDashboardDateRange } from '@/features/dashboard/hooks';
import { useResultsFileUpload } from '@/features/dashboard/sections/useResultsFileUpload';
import { buildChartDates, buildLineChartSeries } from '@/features/executions/utils/chartData';
import { buildStackedExecutionChartData } from '@/features/executions/utils/stackedExecutionChart';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { buildThresholdGuideLines } from '@/features/metrics/lib/chartThresholdGuides';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import {
  invalidateGridQuery,
  queryKeys,
  useAccessToken,
  useDelayedFlag,
  useInvalidateQueries,
  usePersistedStringArray,
  useResolvedFeatureFlags,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildEqFilter,
  buildEqFilterValue,
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildGridQueryInput } from '@/ui/composites/grid/gridUtils';
import { formatStatusLabel } from '@/ui/primitives';
import type {
  ActiveEntityTab,
  EntityStatusKey,
  EntityTrendsPageConfig,
  EntityTrendsPageViewModel,
  ExecutionStatusKey,
} from './useEntityTrendsPage.types';
import type { MetricsRange } from '@/features/metrics';
import type { MetricCardOption } from '@/features/metrics/components/statusMetricCards';
import type { EntityChartTab } from '@/features/shared/components/EntityPageContent';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult } from '@flowqa/shared';

export type { EntityStatusKey, EntityTrendsPageViewModel } from './useEntityTrendsPage.types';

export const useEntityTrendsPage = <TEntityItem, TSummary extends { total: number }>(
  config: EntityTrendsPageConfig<TEntityItem, TSummary>,
): EntityTrendsPageViewModel<TEntityItem> => {
  const [range, setRange] = useState<DashboardRange>('latest');
  const [activeTab, setActiveTab] = useState<ActiveEntityTab>('entity');
  const [chartTab, setChartTab] = useState<EntityChartTab>('status');
  const [suppressChartAnimation, setSuppressChartAnimation] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [tableDeltasEnabled, setTableDeltasEnabled] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<EntityStatusKey[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray(`tag-filter:${config.gridPath}`);
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const [chartSelectedStartDay, setChartSelectedStartDay] = useState<string | null>(null);
  const [chartSelectedEndDay, setChartSelectedEndDay] = useState<string | null>(null);
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useDashboardDateRange(range);
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const deltasEnabled = featureFlags?.deltas ?? true;
  const showDelta = deltasEnabled && !isLatestMode && !isAllMode && previousRangeStartIso !== null;
  const accessToken = useAccessToken();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: selectedProjectId,
    enabled: Boolean(accessToken),
  });
  const projectOptions = useProjectsQuery().data?.items ?? [];
  const uploadProjectIdFieldId = useId();
  const handleUploadRefresh = useCallback(() => {
    void invalidateQueries([
      invalidateGridQuery(config.gridPath),
      invalidateGridQuery(config.executionGridPath),
      { queryKey: config.summaryPrefixKey(), exact: false },
      { queryKey: config.executionSummaryPrefixKey(), exact: false },
      { queryKey: queryKeys.metricsPrefix(), exact: false },
    ]);
  }, [config, invalidateQueries]);
  const uploadFlow = useResultsFileUpload({
    selectedProjectId,
    selectedOrganisationId,
    onRefresh: handleUploadRefresh,
  });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const entityThresholds = config.selectThresholds(thresholds);

  const dateRangeFilters = useMemo(
    () => buildDateRangeFilters('timestamp', rangeStartIso, rangeEndIso),
    [rangeEndIso, rangeStartIso],
  );
  const previousDateRangeFilters = useMemo(
    () => buildDateRangeFilters('timestamp', previousRangeStartIso, previousRangeEndIso),
    [previousRangeEndIso, previousRangeStartIso],
  );
  const selectedDayFilters = useMemo(() => {
    if (!selectedStartDay && !selectedEndDay) return [];
    const start = selectedStartDay ? `${selectedStartDay}T00:00:00.000Z` : null;
    const end = selectedEndDay ? `${selectedEndDay}T23:59:59.999Z` : null;
    return buildDateRangeFilters('timestamp', start, end);
  }, [selectedEndDay, selectedStartDay]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [searchValue]);

  useEffect(() => {
    setSuppressChartAnimation(true);
    const timer = window.setTimeout(() => {
      setSuppressChartAnimation(false);
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [chartTab]);

  const entitySearchFilters = useMemo(
    () => buildContainsFilter(config.entitySearchField, debouncedSearchValue),
    [config.entitySearchField, debouncedSearchValue],
  );
  const executionSearchFilters = useMemo(
    () => buildContainsFilter(config.executionSearchField, debouncedSearchValue),
    [config.executionSearchField, debouncedSearchValue],
  );
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const summaryFilters = useMemo(
    () =>
      isLatestMode
        ? mergeFilters(entitySearchFilters, tagFilters)
        : mergeFilters(dateRangeFilters, executionSearchFilters, tagFilters),
    [dateRangeFilters, entitySearchFilters, executionSearchFilters, isLatestMode, tagFilters],
  );
  const previousSummaryFilters = useMemo(
    () => mergeFilters(previousDateRangeFilters, executionSearchFilters, tagFilters),
    [executionSearchFilters, previousDateRangeFilters, tagFilters],
  );
  const baseExecutionFilters = useMemo(
    () =>
      mergeFilters(
        selectedDayFilters,
        !isLatestMode && !isAllMode ? dateRangeFilters : undefined,
        isLatestMode ? buildEqFilterValue('isLatest', true) : undefined,
        executionSearchFilters,
        tagFilters,
      ),
    [
      dateRangeFilters,
      executionSearchFilters,
      isAllMode,
      isLatestMode,
      selectedDayFilters,
      tagFilters,
    ],
  );
  const summaryInput = useMemo(
    () => buildGridQueryInput({ page: 1, pageSize: 1, sorts: [], filters: summaryFilters }),
    [summaryFilters],
  );
  const previousSummaryInput = useMemo(
    () =>
      isLatestMode
        ? null
        : buildGridQueryInput({
            page: 1,
            pageSize: 1,
            sorts: [],
            filters: previousSummaryFilters,
          }),
    [isLatestMode, previousSummaryFilters],
  );
  const summaryFiltersKey = useMemo(
    () => JSON.stringify(summaryInput.filters),
    [summaryInput.filters],
  );
  const previousSummaryFiltersKey = useMemo(
    () => (previousSummaryInput ? JSON.stringify(previousSummaryInput.filters) : 'latest'),
    [previousSummaryInput],
  );

  const summaryQuery = useQuery<TSummary>({
    queryKey: isLatestMode
      ? config.summaryKey(
          selectedProjectId ?? null,
          selectedOrganisationId ?? null,
          summaryFiltersKey,
        )
      : config.executionSummaryKey(
          selectedProjectId ?? null,
          selectedOrganisationId ?? null,
          summaryFiltersKey,
        ),
    enabled: Boolean(accessToken),
    queryFn: async () =>
      isLatestMode
        ? config.queryLatestSummary(
            selectedProjectId,
            selectedOrganisationId ?? undefined,
            summaryInput,
            accessToken,
          )
        : config.queryExecutionSummary(
            selectedProjectId,
            selectedOrganisationId ?? undefined,
            summaryInput,
            accessToken,
          ),
  });
  const previousSummaryQuery = useQuery<TSummary>({
    queryKey: config.executionSummaryKey(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && !isLatestMode,
    queryFn: async () =>
      config.queryExecutionSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousSummaryInput ?? summaryInput,
        accessToken,
      ),
  });

  const summary = summaryQuery.data ?? null;
  const previousSummary = !isLatestMode ? (previousSummaryQuery.data ?? null) : null;
  const counts = useMemo(() => config.buildCounts(summary), [config, summary]);
  const previousCounts = useMemo(
    () => config.buildCounts(previousSummary),
    [config, previousSummary],
  );
  const deltas = useMemo(
    () => (showDelta && previousSummary ? buildStatusDeltaMap(counts, previousCounts) : undefined),
    [counts, previousCounts, previousSummary, showDelta],
  );
  const metricOptions = useMemo<Array<MetricCardOption<EntityStatusKey>>>(() => {
    const baseOptions = mapStatusOptionsToMetricOptions(
      config.statusOptions,
      resolveMetricTone,
    ) as Array<MetricCardOption<EntityStatusKey>>;
    return [
      ...baseOptions,
      {
        value: 'untested',
        label: formatStatusLabel('untested'),
        tone: resolveMetricTone('untested'),
      },
    ];
  }, [config.statusOptions]);
  const metricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: metricOptions,
        counts,
        deltas,
        total: summary?.total,
        loading: summaryQuery.isFetching,
        showDelta,
      }),
    [counts, deltas, metricOptions, showDelta, summary?.total, summaryQuery.isFetching],
  );
  const statusFilterOptions = useMemo(
    () => [...config.statusOptions, { value: 'untested' as const, label: 'Untested' }],
    [config.statusOptions],
  );

  const entityStatusFilters = useMemo(() => {
    if (selectedStatuses.length === 0) return [];
    const hasUntested = selectedStatuses.includes('untested');
    const statusValues = selectedStatuses.filter(
      (status): status is ExecutionStatusKey => status !== 'untested',
    );
    const filterValues: Array<string | null> = hasUntested ? [null, ...statusValues] : statusValues;
    return buildInFilterWithNull('latestExecutionStatus', filterValues);
  }, [selectedStatuses]);
  const entityTableFilters = useMemo(
    () => mergeFilters(entitySearchFilters, entityStatusFilters, tagFilters),
    [entitySearchFilters, entityStatusFilters, tagFilters],
  );
  const executionStatusFilters = useMemo(() => {
    if (selectedStatuses.length === 0) return [];
    const statusValues = selectedStatuses.filter(
      (status): status is ExecutionStatusKey => status !== 'untested',
    );
    if (statusValues.length === 0) {
      return buildEqFilter('id', 'no-match');
    }
    return buildInFilter('status', statusValues);
  }, [selectedStatuses]);
  const executionStatusSelection = useMemo(
    () => selectedStatuses.filter((status): status is ExecutionStatusKey => status !== 'untested'),
    [selectedStatuses],
  );
  const isUntestedOnly = selectedStatuses.length === 1 && selectedStatuses[0] === 'untested';
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);

  useEffect(() => {
    if (!isLatestMode) {
      setChartSelectedStartDay(null);
      setChartSelectedEndDay(null);
    }
  }, [isLatestMode]);

  const metricsRange = useMemo<MetricsRange>(
    () => (hasCustomDates ? 'all' : isLatestMode ? 'all' : range),
    [hasCustomDates, isLatestMode, range],
  );
  const previousStartDay = previousRangeStartIso?.slice(0, 10) ?? null;
  const previousEndDay = previousRangeEndIso?.slice(0, 10) ?? null;

  const executionMetricsQuery = useMetricsQuery({
    range: metricsRange,
    statuses: executionStatusSelection.length > 0 ? executionStatusSelection : [],
    tags: selectedTags,
    scope: config.metricsScope,
    projectId: selectedProjectId,
    latestOnly: isLatestMode,
    name: debouncedSearchValue,
    startDay: isLatestMode ? null : hasCustomDates ? selectedStartDay : null,
    endDay: isLatestMode ? null : hasCustomDates ? selectedEndDay : null,
    enabled: Boolean(accessToken),
    refetchOnWindowFocus: false,
  });
  const previousMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: executionStatusSelection.length > 0 ? executionStatusSelection : [],
    tags: selectedTags,
    scope: config.metricsScope,
    projectId: selectedProjectId,
    name: debouncedSearchValue,
    startDay: showDelta ? previousStartDay : null,
    endDay: showDelta ? previousEndDay : null,
    enabled: Boolean(accessToken && showDelta),
    refetchOnWindowFocus: false,
  });

  const summaryPassRate = useMemo(() => {
    const total = summary?.total ?? null;
    if (total === null || total <= 0) return null;
    return ((counts.passed ?? 0) / total) * 100;
  }, [counts.passed, summary?.total]);
  const previousSummaryPassRate = useMemo(() => {
    if (!showDelta || !previousSummary) return null;
    const total = previousSummary.total;
    if (total <= 0) return null;
    return ((previousCounts.passed ?? 0) / total) * 100;
  }, [previousCounts.passed, previousSummary, showDelta]);

  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: executionMetricsQuery.data ?? null,
        previousMetrics: showDelta ? (previousMetricsQuery.data ?? null) : null,
        passRateOverride: summaryPassRate,
        previousPassRateOverride: previousSummaryPassRate,
        thresholds: entityThresholds,
        showDelta,
      }),
    [
      entityThresholds,
      executionMetricsQuery.data,
      previousMetricsQuery.data,
      previousSummaryPassRate,
      showDelta,
      summaryPassRate,
    ],
  );

  const stackedChartData = useMemo(() => {
    const baseData = buildStackedExecutionChartData(executionMetricsQuery.data);
    if (!baseData) return null;
    if (!isUntestedOnly) return baseData;
    const emptyData = baseData.data.map((datum) => ({
      ...datum,
      passed: 0,
      failed: 0,
      errored: 0,
      skipped: 0,
    }));
    return { ...baseData, data: emptyData };
  }, [executionMetricsQuery.data, isUntestedOnly]);

  const chartDates = useMemo(() => {
    const labels = executionMetricsQuery.data?.metricsRangeLabels ?? [];
    const dates = executionMetricsQuery.data?.metricsRangeDates ?? [];
    return buildChartDates(labels, dates);
  }, [
    executionMetricsQuery.data?.metricsRangeDates,
    executionMetricsQuery.data?.metricsRangeLabels,
  ]);

  const chartFilterRange = useMemo(() => {
    if (!isLatestMode || !chartSelectedStartDay) return null;
    return { start: chartSelectedStartDay, end: chartSelectedEndDay ?? chartSelectedStartDay };
  }, [chartSelectedEndDay, chartSelectedStartDay, isLatestMode]);
  const chartFilterIndices = useMemo(() => {
    if (!chartFilterRange) return null;
    const { start, end } = chartFilterRange;
    return chartDates
      .map((date, index) => (date >= start && date <= end ? index : -1))
      .filter((index) => index >= 0);
  }, [chartDates, chartFilterRange]);
  const filteredStackedChartData = useMemo(() => {
    if (!stackedChartData || !chartFilterIndices) return stackedChartData;
    const filteredData = chartFilterIndices
      .map((index) => stackedChartData.data[index])
      .filter(Boolean);
    return { ...stackedChartData, data: filteredData };
  }, [chartFilterIndices, stackedChartData]);

  const metricsStartDay = useMemo(() => chartDates[0] ?? null, [chartDates]);
  const metricsEndDay = useMemo(
    () => (chartDates.length > 0 ? (chartDates[chartDates.length - 1] ?? null) : null),
    [chartDates],
  );
  const allModeExecutionDateFilters = useMemo(
    () =>
      isAllMode && metricsStartDay && metricsEndDay
        ? buildDateRangeFilters(
            'timestamp',
            `${metricsStartDay}T00:00:00.000Z`,
            `${metricsEndDay}T23:59:59.999Z`,
          )
        : undefined,
    [isAllMode, metricsEndDay, metricsStartDay],
  );
  const executionFilters = useMemo(
    () => mergeFilters(baseExecutionFilters, allModeExecutionDateFilters),
    [allModeExecutionDateFilters, baseExecutionFilters],
  );
  const pickerStartDay = useMemo(
    () =>
      selectedStartDay ??
      (range !== 'latest' && range !== 'all'
        ? (rangeStartIso?.slice(0, 10) ?? null)
        : metricsStartDay),
    [metricsStartDay, range, rangeStartIso, selectedStartDay],
  );
  const pickerEndDay = useMemo(
    () =>
      selectedEndDay ??
      (range !== 'latest' && range !== 'all' ? (rangeEndIso?.slice(0, 10) ?? null) : metricsEndDay),
    [metricsEndDay, range, rangeEndIso, selectedEndDay],
  );

  const handleSelectedStartDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleSelectedEndDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );
  const handleChartDateClear = useCallback(() => {
    setChartSelectedStartDay(null);
    setChartSelectedEndDay(null);
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);
  const handleChartSelect = useCallback(
    (dataIndex: number) => {
      const day = chartDates[dataIndex];
      if (!day) return;
      if (isLatestMode) {
        if (chartSelectedStartDay) {
          setChartSelectedStartDay(null);
          setChartSelectedEndDay(null);
          setSelectedStartDay(null);
          setSelectedEndDay(null);
        } else {
          setChartSelectedStartDay(day);
          setChartSelectedEndDay(day);
          setSelectedStartDay(day);
          setSelectedEndDay(day);
        }
      } else {
        const isSameDay = selectedStartDay === day && selectedEndDay === day;
        if (isSameDay) {
          setSelectedStartDay(null);
          setSelectedEndDay(null);
        } else {
          setSelectedStartDay(day);
          setSelectedEndDay(day);
        }
      }
    },
    [chartDates, chartSelectedStartDay, isLatestMode, selectedEndDay, selectedStartDay],
  );

  const chartHeight = 360;
  const showChartSkeleton = useDelayedFlag(executionMetricsQuery.isFetching, 250);
  const lineChartLabels = executionMetricsQuery.data?.metricsRangeLabels ?? [];
  const passRateChartData = useMemo(() => {
    const series = buildLineChartSeries(
      executionMetricsQuery.data?.series.filteredPassRate,
      lineChartLabels,
      chartDates,
      'passRate',
    );
    if (series) return series;
    return executionMetricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    executionMetricsQuery.data?.series.filteredPassRate,
    executionMetricsQuery.isFetched,
    lineChartLabels,
  ]);
  const frequencyChartData = useMemo(() => {
    const series = buildLineChartSeries(
      executionMetricsQuery.data?.series.executionTrendMetrics,
      lineChartLabels,
      chartDates,
      'frequency',
    );
    if (series) return series;
    return executionMetricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    executionMetricsQuery.data?.series.executionTrendMetrics,
    executionMetricsQuery.isFetched,
    lineChartLabels,
  ]);
  const durationChartData = useMemo(() => {
    const series = buildLineChartSeries(
      executionMetricsQuery.data?.series.executionDurationAverage,
      lineChartLabels,
      chartDates,
      'duration',
    );
    if (series) return series;
    return executionMetricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    executionMetricsQuery.data?.series.executionDurationAverage,
    executionMetricsQuery.isFetched,
    lineChartLabels,
  ]);
  const filteredPassRateChartData = useMemo(() => {
    if (!passRateChartData || !chartFilterIndices) return passRateChartData;
    return chartFilterIndices.map((index) => passRateChartData[index]).filter(Boolean);
  }, [chartFilterIndices, passRateChartData]);
  const filteredFrequencyChartData = useMemo(() => {
    if (!frequencyChartData || !chartFilterIndices) return frequencyChartData;
    return chartFilterIndices.map((index) => frequencyChartData[index]).filter(Boolean);
  }, [chartFilterIndices, frequencyChartData]);
  const filteredDurationChartData = useMemo(() => {
    if (!durationChartData || !chartFilterIndices) return durationChartData;
    return chartFilterIndices.map((index) => durationChartData[index]).filter(Boolean);
  }, [chartFilterIndices, durationChartData]);
  const showLineChartPoints = useMemo(() => {
    const start = isLatestMode ? chartSelectedStartDay : selectedStartDay;
    const end = isLatestMode ? (chartSelectedEndDay ?? chartSelectedStartDay) : selectedEndDay;
    return Boolean(start && end && start === end);
  }, [chartSelectedEndDay, chartSelectedStartDay, isLatestMode, selectedEndDay, selectedStartDay]);

  const queryKey = useMemo(
    () =>
      queryKeys.dataGrid(
        config.gridPath,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        tableDeltasEnabled ? 'table-deltas-on' : 'table-deltas-off',
      ),
    [
      config.gridPath,
      previousRangeEndIso,
      previousRangeStartIso,
      rangeEndIso,
      rangeStartIso,
      tableDeltasEnabled,
    ],
  );
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TEntityItem>> =>
      config.queryEntityTable(input, {
        selectedProjectId,
        selectedOrganisationId: selectedOrganisationId ?? null,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
        tableDeltasEnabled,
      }),
    [
      config,
      deltasEnabled,
      previousRangeEndIso,
      previousRangeStartIso,
      rangeEndIso,
      rangeStartIso,
      selectedOrganisationId,
      selectedProjectId,
      tableDeltasEnabled,
    ],
  );

  const showPageSkeleton = Boolean(accessToken) && summaryQuery.isLoading && !summary;
  const passRateGuideLines = useMemo(
    () => buildThresholdGuideLines(entityThresholds.passRate),
    [entityThresholds.passRate],
  );
  const frequencyGuideLines = useMemo(
    () => buildThresholdGuideLines(entityThresholds.frequency),
    [entityThresholds.frequency],
  );
  const durationGuideLines = useMemo(
    () => buildThresholdGuideLines(entityThresholds.avgDuration),
    [entityThresholds.avgDuration],
  );

  const handleRangeChange = useCallback((nextRange: DashboardRange) => {
    setRange(nextRange);
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);

  const handleStatusToggle = useCallback((status: EntityStatusKey) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status],
    );
  }, []);
  const handleStatusesChange = useCallback((nextStatuses: EntityStatusKey[]) => {
    setSelectedStatuses(nextStatuses);
  }, []);

  return {
    loadingMessage: config.loadingMessage,
    range,
    chartTab,
    activeTab,
    searchValue,
    pickerStartDay,
    pickerEndDay,
    hasCustomDates,
    isLatestMode,
    metricCards,
    selectedStatuses,
    statusFilterOptions,
    selectedTags,
    tagOptions,
    tagOptionsLoading,
    metricValueItems,
    chartHeight,
    showChartSkeleton,
    suppressChartAnimation,
    showLineChartPoints,
    filteredStackedChartData,
    filteredPassRateChartData,
    filteredFrequencyChartData,
    filteredDurationChartData,
    passRateGuideLines,
    frequencyGuideLines,
    durationGuideLines,
    deltasEnabled,
    tableDeltasEnabled,
    queryKey,
    queryFn,
    entityTableFilters,
    executionFilters,
    executionStatusFilters,
    showChartDateClear: hasCustomDates,
    showPageSkeleton,
    selectedProjectId,
    projectOptions,
    uploadProjectIdFieldId,
    uploadFlow,
    handleRangeChange,
    handleSearchChange: setSearchValue,
    handleChartTabChange: setChartTab,
    handleActiveTabChange: setActiveTab,
    handleStatusToggle,
    handleStatusesChange,
    handleTagsChange: setSelectedTags,
    handleChartSelect,
    handleSelectedStartDayChange,
    handleSelectedEndDayChange,
    handleTableDeltasEnabledChange: setTableDeltasEnabled,
    handleChartDateClear,
  };
};
